import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import type { AgentChatMessage } from '~/api/agentChat'
import RhapFeedbackButton, {
  FeedbackType,
} from '~/components/common/RhapFeedbackButton'
import {
  InputMethod,
  trackEvent,
  TrackingEvent,
} from '~/utils/trackingProvider'

import { useAgentChat } from '../AgentChatContext'
import {
  NEGATIVE_FEEDBACK_REASON,
  type NegativeFeedbackReasonKey,
} from './agentChatFeedbackReasons'
import { AgentFeedbackMessage } from './AgentFeedbackMessage'
import { AgentResponseButton } from './AgentResponseButton'
import { MarkdownRenderer } from './MarkdownComponents'
import { MessageAttachment } from './MessageAttachment'
import { MessageBubble } from './MessageBubble'
import {
  parseSuggestedPromptsSection,
  stripPartialSuggestedPromptsSection,
} from './parseSuggestedPromptsSection'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChatMessage.module.scss'

interface AgentMessageBubbleProps {
  isLastMessageInChat?: boolean
  isMostRecent?: boolean
  message: AgentChatMessage
  sessionId: string | null
}

const getBaseMeta = (message: AgentChatMessage, sessionId: string | null) => ({
  messageId: message.id,
  messageContent: message.content,
  agent_session_id: sessionId,
  invocationId: message.invocationId,
  agent_name: message.agent,
})

export const AgentMessageBubble: React.FC<AgentMessageBubbleProps> = ({
  isLastMessageInChat = false,
  isMostRecent = false,
  message,
  sessionId,
}) => {
  const { t } = useTranslation('Agent')
  const { sendMessage } = useAgentChat()
  const [feedback, setFeedback] = useState<FeedbackType | null>(null)
  const [negativeReason, setNegativeReason] =
    useState<NegativeFeedbackReasonKey | null>(null)
  const feedbackEventEmittedRef = useRef(false)
  const baseMetaRef = useRef(getBaseMeta(message, sessionId))
  baseMetaRef.current = getBaseMeta(message, sessionId)
  const baseMeta = baseMetaRef.current

  const emitPositive = () => {
    if (feedbackEventEmittedRef.current) return
    trackEvent(TrackingEvent.AI_ASSISTANT_FEEDBACK_POSITIVE, baseMeta)
    feedbackEventEmittedRef.current = true
  }

  const emitNegative = (reason: string, feedbackText?: string) => {
    if (feedbackEventEmittedRef.current) return
    trackEvent(TrackingEvent.AI_ASSISTANT_FEEDBACK_NEGATIVE, {
      ...baseMeta,
      reason,
      ...(feedbackText !== undefined && feedbackText !== ''
        ? { feedback_text: feedbackText }
        : {}),
    })
    feedbackEventEmittedRef.current = true
  }

  useEffect(
    () => () => {
      if (
        feedback === FeedbackType.NEGATIVE &&
        !feedbackEventEmittedRef.current
      ) {
        trackEvent(TrackingEvent.AI_ASSISTANT_FEEDBACK_NEGATIVE, {
          ...baseMetaRef.current,
          reason: NEGATIVE_FEEDBACK_REASON.NO_REASON_GIVEN,
        })
      }
    },
    [feedback],
  )

  const handleFeedback = (type: FeedbackType) => {
    setFeedback(type)
    if (type === FeedbackType.POSITIVE) {
      emitPositive()
    }
  }

  const handleReasonClick = (reasonKey: NegativeFeedbackReasonKey) => {
    setNegativeReason(reasonKey)
    emitNegative(reasonKey)
  }

  const handleSendFeedback = (feedbackText: string) => {
    emitNegative(NEGATIVE_FEEDBACK_REASON.OTHER, feedbackText)
    setNegativeReason(NEGATIVE_FEEDBACK_REASON.OTHER)
  }

  const isStreaming = message.isComplete !== true && message.partial !== false
  const fullParsed =
    message.isLoading || typeof message.content !== 'string'
      ? { contentWithoutSection: message.content ?? '', suggestedPrompts: [] }
      : parseSuggestedPromptsSection(message.content)
  const contentForBubble =
    message.isLoading || typeof message.content !== 'string'
      ? (message.content ?? '')
      : isStreaming
        ? stripPartialSuggestedPromptsSection(message.content)
        : fullParsed.contentWithoutSection
  const { suggestedPrompts } = fullParsed

  return (
    <>
      <MessageBubble sender="agent">
        {message.isLoading ? (
          <div className={styles.loadingMessage}>
            <div className={styles.dots}>
              <div className={styles.dot} />
              <div className={styles.dot} />
              <div className={styles.dot} />
            </div>
          </div>
        ) : (
          <>
            <MarkdownRenderer
              content={contentForBubble}
              sessionId={sessionId}
            />
            {message.attachments?.map((attachment, idx) => (
              <MessageAttachment
                attachment={attachment}
                // eslint-disable-next-line react/no-array-index-key
                key={`${message.id}-${attachment.mimeType}-${idx}`}
              />
            ))}
            <div className={styles.messageFooter}>
              {isMostRecent &&
                (message.isComplete === true || message.partial === false) && (
                  <div className={styles.feedbackContainer}>
                    <span className={styles.feedbackQuestion}>
                      {t('FEEDBACK_QUESTION')}
                    </span>
                    <div className={styles.feedbackButtons}>
                      <RhapFeedbackButton
                        disabled={feedback !== null}
                        isSelected={feedback === FeedbackType.POSITIVE}
                        type={FeedbackType.POSITIVE}
                        onClick={() => handleFeedback(FeedbackType.POSITIVE)}
                      />
                      <RhapFeedbackButton
                        disabled={feedback !== null}
                        isSelected={feedback === FeedbackType.NEGATIVE}
                        type={FeedbackType.NEGATIVE}
                        onClick={() => handleFeedback(FeedbackType.NEGATIVE)}
                      />
                    </div>
                  </div>
                )}
              <div className={styles.messageTimestamp}>{message.timestamp}</div>
            </div>
          </>
        )}
      </MessageBubble>
      <AgentFeedbackMessage
        feedback={feedback}
        negativeReason={negativeReason}
        onReasonClick={handleReasonClick}
        onSendFeedback={handleSendFeedback}
      />
      {suggestedPrompts.length > 0 && isLastMessageInChat && (
        <div
          className={classNames(
            styles.messageContainer,
            styles.agentMessage,
            styles.suggestedPromptsContainer,
          )}
        >
          <MessageBubble sender="agent">
            <div className={styles.suggestedPromptsBubbleContent}>
              <p className={styles.suggestedPromptsIntro}>
                {t('WOULD_YOU_LIKE_ME_TO')}
              </p>
              <div className={styles.suggestedPromptsList}>
                {suggestedPrompts.map((promptText, index) => (
                  <div
                    className={styles.suggestedPromptRow}
                    key={`${message.id}-prompt-${promptText}`}
                  >
                    <span className={styles.suggestedPromptNumber}>
                      {index + 1}.
                    </span>
                    <AgentResponseButton
                      onClick={() =>
                        sendMessage({
                          content: promptText.trim(),
                          inputMethod: InputMethod.FOLLOW_UP_PROMPT,
                        })
                      }
                    >
                      {promptText}
                    </AgentResponseButton>
                  </div>
                ))}
              </div>
            </div>
          </MessageBubble>
        </div>
      )}
    </>
  )
}

export default AgentMessageBubble
